import { Component } from '@theme/component';
import EmblaCarousel from '@theme/embla-carousel';
import { bindGrabCursor } from '@theme/be-embla-grab-cursor';

/**
 * A horizontal carousel with previous/next controls, powered by the vendored
 * Embla library (assets/embla-carousel.js) — same approach as
 * assets/be-home-hero-carousel.js — so advancing past either end loops
 * seamlessly instead of smooth-scrolling all the way back.
 *
 * Embla only loops when the remaining slides can cover the viewport while it
 * repositions one; with a handful of cards at three columns they can't, and
 * Embla silently rebuilds itself with `loop: false`. To guarantee the loop,
 * one inert copy of the slide set is appended before init (hidden from
 * assistive tech and stripped of theme-editor block attributes).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The overflow-hidden element Embla measures.
 * @property {HTMLElement} track - The slide container Embla translates.
 * @property {HTMLElement} [dots] - Optional mobile pagination dot container.
 * @property {HTMLElement} [counter] - Optional mobile slide-counter caption.
 *
 * @extends {Component<Refs>}
 */
class VineyardCarouselComponent extends Component {
  requiredRefs = ['viewport', 'track'];

  /** @type {ReturnType<typeof EmblaCarousel> | undefined} */
  #embla;

  /** The number of real (non-clone) slides. */
  #slideCount = 0;

  /**
   * Reports the active slide's media height as --be-vineyards-media-h on the
   * host. The media is viewport-height driven (100svh minus the header, name,
   * and blurb — blocks/vineyard-card.liquid), so the section CSS can't derive
   * it from the carousel width; the arrows, dots, and counter position
   * against this var. #syncBlurbRowMin keeps the height uniform across
   * slides; retargeting on every selection is a safety net for residual
   * differences (e.g. a name that wraps to two lines).
   *
   * @type {ResizeObserver}
   */
  #mediaObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
    this.style.setProperty('--be-vineyards-media-h', `${Math.round(height)}px`);
  });

  connectedCallback() {
    super.connectedCallback();
    this.#init();

    // Webfonts can rewrap the blurbs after first layout.
    document.fonts.ready.then(() => {
      if (this.isConnected) this.#syncBlurbRowMin();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  updatedCallback() {
    super.updatedCallback();

    // Section Rendering API / editor morphs can change the blocks — rebuild
    // so the clones and Embla's measurements match the new DOM.
    this.#destroy();
    this.#init();
  }

  /**
   * Scrolls to the next slide, wrapping from the last to the first.
   */
  next() {
    this.#embla?.scrollNext();
  }

  /**
   * Scrolls to the previous slide, wrapping from the first to the last.
   */
  previous() {
    this.#embla?.scrollPrev();
  }

  /**
   * Scrolls to the slide of the clicked pagination dot.
   *
   * @param {PointerEvent} event
   */
  select(event) {
    const dot = event.target instanceof Element && event.target.closest('[data-index]');

    if (!(dot instanceof HTMLElement)) return;

    const index = Number(dot.dataset.index);

    if (!Number.isNaN(index)) this.#embla?.scrollTo(index);
  }

  #init() {
    const { viewport, track } = this.refs;
    if (!track.childElementCount) return;

    this.#slideCount = track.childElementCount;
    this.#cloneSlides(track);

    const rtl = document.documentElement.dir === 'rtl';

    this.#embla = EmblaCarousel(viewport, {
      loop: true,
      // Let drag momentum carry past the adjacent snap and soften the
      // settle — see the note in be-embla-carousel.js.
      skipSnaps: true,
      duration: 50,
      // The viewport is full-bleed (margin-inline: -page-margin). On desktop
      // resting slides are pulled back in to the page margin; on mobile the
      // slides are themselves full-bleed, so the offset is 0. The offset is
      // measured from the DOM rather than parsed out of the CSS variable,
      // and the function re-runs on every Embla (re)measure, keeping
      // responsive margin changes correct.
      align: () => {
        if (window.matchMedia('(max-width: 749px)').matches) return 0;

        const viewportRect = viewport.getBoundingClientRect();
        const hostRect = this.getBoundingClientRect();
        return rtl ? viewportRect.right - hostRect.right : hostRect.left - viewportRect.left;
      },
      direction: rtl ? 'rtl' : 'ltr',
    });

    bindGrabCursor(this.#embla);
    this.#embla.on('select', this.#updateActiveSlide);
    this.#embla.on('reInit', this.#syncBlurbRowMin);
    this.#embla.on('reInit', this.#updateActiveSlide);
    this.#syncBlurbRowMin();
    this.#updateActiveSlide();
  }

  /**
   * Equalizes the media height across slides. Every card's blurb row
   * reserves the tallest blurb's margin-box height (--be-vineyard-blurb-min,
   * consumed by blocks/vineyard-card.liquid), so the 1fr media row resolves
   * identically even when blurb lengths differ. Runs again on Embla's
   * reInit (viewport resizes rewrap the text) and once webfonts settle.
   * The blurb keeps its natural height (align-self: start), so re-measuring
   * with a previous minimum still applied stays accurate.
   */
  #syncBlurbRowMin = () => {
    const blurbs = this.refs.track.querySelectorAll(
      ':scope > :not([data-loop-clone]) .be-vineyard-card__blurb'
    );

    if (!blurbs.length) {
      this.style.removeProperty('--be-vineyard-blurb-min');
      return;
    }

    const tallest = Math.max(
      ...Array.from(blurbs, (blurb) => {
        const marginTop = parseFloat(getComputedStyle(blurb).marginBlockStart) || 0;
        return blurb.getBoundingClientRect().height + marginTop;
      })
    );

    this.style.setProperty('--be-vineyard-blurb-min', `${Math.ceil(tallest)}px`);
  };

  /**
   * Reflects the active slide in the pagination dots and the slide counter.
   * Clones share snap points with their originals, so the index wraps to the
   * real slide count.
   */
  #updateActiveSlide = () => {
    if (!this.#embla || !this.#slideCount) return;

    this.#observeActiveMedia();

    const index = this.#embla.selectedScrollSnap() % this.#slideCount;
    const { dots, counter } = this.refs;

    if (counter) counter.textContent = String(index + 1).padStart(2, '0');

    if (dots) {
      for (const [dotIndex, dot] of Array.from(dots.children).entries()) {
        if (dotIndex === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }
    }
  };

  /**
   * Points the media observer at the active slide's media element (initial
   * measurement fires on `observe`; later deliveries track viewport
   * resizes). Clones are fine targets — they render at the same height as
   * their originals.
   */
  #observeActiveMedia() {
    if (!this.#embla) return;

    const slide = this.#embla.slideNodes()[this.#embla.selectedScrollSnap()];
    const media = slide?.querySelector('.be-vineyard-card__media');

    if (!media) return;

    this.#mediaObserver.disconnect();
    this.#mediaObserver.observe(media);
  }

  /**
   * Appends one inert copy of the slide set so Embla always has enough
   * slides to keep the viewport covered while it repositions them.
   * @param {HTMLElement} track
   */
  #cloneSlides(track) {
    for (const slide of Array.from(track.children)) {
      const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
      clone.setAttribute('data-loop-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('data-shopify-editor-block');
      track.append(clone);
    }
  }

  #destroy() {
    this.#mediaObserver.disconnect();
    this.#embla?.destroy();
    this.#embla = undefined;

    for (const clone of this.querySelectorAll('[data-loop-clone]')) {
      clone.remove();
    }
  }
}

if (!customElements.get('vineyard-carousel-component')) {
  customElements.define('vineyard-carousel-component', VineyardCarouselComponent);
}
